const React = require('react');
const { createElement: h, useState, useEffect } = React;
const UsageChart = require('./UsageChart');
const { loadUsageHistory } = require('../lib/authStorage');

var BUCKETS = [
  { key: 'hour',  label: 'Time' },
  { key: 'day',   label: 'Dag' },
  { key: 'week',  label: 'Uge' },
  { key: 'month', label: 'Måned' }
];

var BUCKET_NOUN = {
  hour:  'time',
  day:   'dag',
  week:  'uge',
  month: 'måned'
};

function formatNumber(n) {
  if (!n || n < 1) return '0';
  return Math.round(n).toLocaleString('da-DK');
}

function rateLabel(total, count, bucket) {
  if (!count) return '0 / ' + BUCKET_NOUN[bucket];
  var avg = total / count;
  return formatNumber(avg) + ' / ' + BUCKET_NOUN[bucket];
}

function StatCard(props) {
  return h('div', { className: 'usp-stat-card' },
    h('span', { className: 'usp-stat-card__label' }, props.label),
    h('span', { className: 'usp-stat-card__value' }, props.value),
    props.hint ? h('span', { className: 'usp-stat-card__hint' }, props.hint) : null
  );
}

function BreakdownTable(props) {
  var rows = Array.isArray(props.rows) ? props.rows : [];
  var valueKey = props.valueKey || 'nodeExecutions';
  var valueSuffix = props.valueSuffix || '';

  if (rows.length === 0) {
    return h('p', { className: 'usp-breakdown__empty' }, 'Ingen kørsler i denne periode endnu.');
  }
  var max = rows.reduce(function(m, r) {
    var v = r[valueKey] || 0;
    return v > m ? v : m;
  }, 0);

  return h('div', { className: 'usp-breakdown__table' },
    rows.map(function(row, idx) {
      var v = row[valueKey] || 0;
      var pct = max > 0 ? (v / max) * 100 : 0;
      var rank = idx + 1;
      return h('div', { className: 'usp-breakdown__row', key: row.key },
        h('span', { className: 'usp-breakdown__rank' }, '#' + rank),
        h('span', { className: 'usp-breakdown__name', title: row.key },
          props.labelFn ? props.labelFn(row.key) : row.key
        ),
        h('div', { className: 'usp-breakdown__bar' },
          h('div', { className: 'usp-breakdown__fill', style: { width: pct + '%' } })
        ),
        h('span', { className: 'usp-breakdown__value' },
          formatNumber(v),
          valueSuffix ? h('span', { className: 'usp-breakdown__value-suffix' }, ' ' + valueSuffix) : null
        )
      );
    })
  );
}

function workflowLabel(workflows) {
  var byId = {};
  (workflows || []).forEach(function(w) {
    if (w && w.id) byId[w.id] = w.title || w.name || w.id;
  });
  return function(id) {
    if (id === 'unknown') return '(ukendt workflow)';
    return byId[id] || id.slice(0, 8) + '…';
  };
}

function UsageHistory(props) {
  var [bucket, setBucket] = useState('day');
  var [data, setData]     = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError]   = useState(null);

  useEffect(function() {
    var cancelled = false;
    setLoading(true);
    setError(null);
    loadUsageHistory(bucket)
      .then(function(res) { if (!cancelled) { setData(res); setLoading(false); } })
      .catch(function(e)  { if (!cancelled) { setError(e && e.message ? e.message : 'Kunne ikke hente data'); setLoading(false); } });
    return function() { cancelled = true; };
  }, [bucket]);

  var totals = (data && data.totals) || { workflowRuns: 0, nodeExecutions: 0, failed: 0, peakRuns: 0, peakNodes: 0, avgNodesPerRun: 0 };
  var points = (data && data.points) || [];
  var breakdown = (data && data.breakdown) || { byWorkflow: [], byNodeType: [] };
  var range = (data && data.range) || 0;

  return h('div', { className: 'usp-section usp-history' },
    h('div', { className: 'usp-history__header' },
      h('h2', { className: 'usp-section__title' }, 'Eksekverings-historik'),
      h('div', { className: 'usp-history__tabs' },
        BUCKETS.map(function(b) {
          return h('button', {
            key: b.key,
            className: 'usp-history__tab' + (bucket === b.key ? ' usp-history__tab--active' : ''),
            onClick: function() { setBucket(b.key); }
          }, b.label);
        })
      )
    ),

    error ? h('p', { className: 'usp-history__error' }, error) : null,

    h('div', { className: 'usp-stat-grid' },
      h(StatCard, {
        label: 'Workflow-kørsler',
        value: formatNumber(totals.workflowRuns),
        hint: rateLabel(totals.workflowRuns, range, bucket) + ' i snit'
      }),
      h(StatCard, {
        label: 'Knude-eksekveringer',
        value: formatNumber(totals.nodeExecutions),
        hint: rateLabel(totals.nodeExecutions, range, bucket) + ' i snit'
      }),
      h(StatCard, {
        label: 'Peak (' + BUCKET_NOUN[bucket] + ')',
        value: formatNumber(totals.peakNodes),
        hint: 'højeste enkelt-' + BUCKET_NOUN[bucket]
      }),
      h(StatCard, {
        label: 'Snit pr. kørsel',
        value: formatNumber(totals.avgNodesPerRun) + ' knuder',
        hint: totals.failed > 0 ? formatNumber(totals.failed) + ' fejlede' : 'ingen fejl'
      })
    ),

    loading
      ? h('p', { className: 'usp-history__loading' }, 'Indlæser…')
      : h('div', { className: 'usp-chart-grid' },
          h(UsageChart, {
            title: 'Workflow-kørsler pr. ' + BUCKET_NOUN[bucket],
            bucket: bucket, points: points, dataKey: 'workflowRuns', color: '#a67c52'
          }),
          h(UsageChart, {
            title: 'Knude-eksekveringer pr. ' + BUCKET_NOUN[bucket],
            bucket: bucket, points: points, dataKey: 'nodeExecutions', color: '#7c3aed'
          })
        ),

    // Drill-down: WHY was the count high — which workflows / node types?
    !loading ? h('div', { className: 'usp-breakdown-grid' },
      h('div', { className: 'usp-breakdown' },
        h('h3', { className: 'usp-breakdown__title' }, 'Mest kørte workflows'),
        h('p', { className: 'usp-breakdown__hint' }, 'Rangeret efter antal kørsler'),
        h(BreakdownTable, {
          rows: breakdown.byWorkflow,
          labelFn: workflowLabel(props.workflows),
          valueKey: 'workflowRuns',
          valueSuffix: 'kørsler'
        })
      ),
      h('div', { className: 'usp-breakdown' },
        h('h3', { className: 'usp-breakdown__title' }, 'Mest brugte knude-typer'),
        h('p', { className: 'usp-breakdown__hint' }, 'Rangeret efter antal eksekveringer'),
        h(BreakdownTable, {
          rows: breakdown.byNodeType,
          valueKey: 'nodeExecutions',
          valueSuffix: 'kørsler'
        })
      )
    ) : null
  );
}

module.exports = UsageHistory;
